// Everything the signed-in person pays Greenback for, keyed by PAYER rather
// than by the business or firm in the request headers (PRD 114 §6.1): their
// personal Pro, every Business group they own, and the firm they own. The
// list feeds Manage subscription (/subscription) and the sidebar badge.
//
// THE SERVER DECIDES WHAT IS SHOWN. `GET /billing/accounts` returns only rows
// the viewer may manage, already filtered by Billing::AlertVisibility: a
// firm-paid group carries no amounts or failures, a group the viewer does not
// own is not there at all. The page invents no rule of its own.
//
// EVERY FIELD IS READ DEFENSIVELY. The web deploys before the api that serves
// this (web ahead of api ships dark), so a missing endpoint is a thrown 404
// and the callers render today's page and badge; a value this web does not
// know (a newer status, rail or action) reads as absent rather than as a
// claim, so an older bundle never names a state it cannot describe.
//
// apiFetch never throws on an HTTP status, so every response is parsed:
// refusals come back as SubscriptionApiError, the same error the personal
// subscription calls throw.

import {
  INVALID_RESPONSE_CODE,
  SubscriptionApiError,
  parseBillingData,
  requireWebUrl,
  sendBillingRequest,
} from './subscriptionService';

export type BillingAccountKind = 'personal' | 'group' | 'firm';

/**
 * How the account's bill travels. `store` is personal Pro RevenueCat still
 * bills (active, retrying a renewal, or cancelled with time left): changed
 * where it was bought, never on Stripe's portal.
 */
export type BillingAccountRail = 'card' | 'invoice' | 'firm_paid' | 'comp' | 'store' | 'none';

export type BillingAccountStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'paused'
  | 'cancelling'
  | 'payment_needed'
  | 'lapsed'
  | 'needs_payment_method'
  | 'none';

/** What fixes an attention item. 1a has none in the app: the page points at the account's own surface. */
export type BillingAttentionTask = 'card' | 'pay' | 'resume';

/** What a Manage subscription deep link may ask for (PRD 114 §4.5); later phases add the last three. */
export type BillingDeepLinkTask = BillingAttentionTask | 'cancel' | 'keep' | 'invoices';

/**
 * What the page may offer for an account in 1a: Stripe's hosted portal (a
 * card-billed group, or personal Pro) or the practice's own billing page.
 */
export type BillingAccountAction = 'stripe_portal' | 'firm_billing';

export interface BillingAttention {
  /** `payment_failed`, `paused`, `card_needed`, `confirm_payment`, or a newer code. */
  code: string;
  task: BillingAttentionTask | null;
}

/**
 * The Business group to reinstate for personal Pro that nothing else gives
 * the person any more (PRD 114 decision 8): its Business bundled their Pro
 * and has stopped (paused, held unpaid, or lapsed), so Manage subscription
 * points them back at it rather than selling Pro on its own, which
 * reinstating the group would then pay for twice.
 */
export interface BillingReinstateWith {
  /** The group's account key, `group:<uuid>`: a group the list itself sends. */
  key: string;
  name: string | null;
  /**
   * What the group's own row asks: `resume` for a pause, `card` for a
   * subscription Stripe holds unpaid; null for a lapsed group, whose way back
   * is a new checkout on its billing card.
   */
  task: BillingAttentionTask | null;
}

/** A client business a practice pays Greenback for, by the practice's name for it. Never an amount. */
export interface FirmBilledClient {
  name: string;
  /** The practice bills the client for it (a firm_billed_rebilled engagement). */
  rebilled: boolean;
}

export interface BillingAccountBusiness {
  id: string;
  name: string | null;
  /**
   * The viewer owns this member business. A group names every member the
   * viewer has books access to, owned first and then by name, not only those
   * they own; a link into the books opens one they own, where the billing
   * card is theirs to act on. False when the api does not say.
   */
  owner: boolean;
}

export interface BillingAccount {
  /** `personal`, `group:<uuid>` or `firm:<uuid>`. */
  key: string;
  kind: BillingAccountKind;
  /** The group's or firm's uuid; null for personal. */
  id: string | null;
  name: string | null;
  /** A group's member businesses the viewer may open; empty for an unnamed signup group with none yet. */
  businesses: BillingAccountBusiness[];
  /**
   * How many of a group's members are not named to the viewer, who has no
   * books access to them: 0 when every member is named. Null on personal and
   * firm rows, and from an api that does not send it.
   */
  other_businesses_count: number | null;
  /** Null for a rail this web does not know. */
  rail: BillingAccountRail | null;
  /**
   * The practice that pays for a firm-paid group, by name only (never its
   * amounts, card or failures). Null on every other row, including that
   * group's own card row, and from an api that does not send it.
   */
  paid_by: { name: string } | null;
  /** Null for a status this web does not know. */
  status: BillingAccountStatus | null;
  current_period_end: string | null;
  cancel_at: string | null;
  /** Personal Pro that comes with a Business group the person owns. */
  included_with: { key: string; name: string | null } | null;
  /**
   * The personal row's only: the stopped group to reinstate for Pro nothing
   * else gives the person. Null on every other row, while anything else
   * gives them Pro, and from an api that does not send it.
   */
  reinstate_with: BillingReinstateWith | null;
  /** Complimentary or open-signup access, until when. */
  free_until: string | null;
  /**
   * A firm's seat count: the clients on its card (active, firm-paid, Stripe
   * rail), the same ones its grace's expiry moves to billing themselves.
   */
  seats: number | null;
  /**
   * A practice past due or lapsed: the days before its grace runs out, its
   * own subscription ends and any clients on its card (`seats`) move to
   * billing themselves (Firms::BillingAlert), 0 once it has. Null
   * when no grace started, on every other row, and from an api that does
   * not send it.
   */
  days_remaining: number | null;
  /**
   * A practice's only: the clients it pays Greenback for (active firm_billed
   * and firm_billed_rebilled engagements), sorted by name; empty when it
   * pays for none. Null on every other row, and from an api that does not
   * send it.
   */
  firm_billed_clients: FirmBilledClient[] | null;
  can_manage: boolean;
  attention: BillingAttention | null;
  actions: BillingAccountAction[];
}

/** Whether the in-app tasks are switched on per kind. All false in 1a. */
export interface BillingSelfServe {
  personal: boolean;
  group: boolean;
  firm: boolean;
}

export interface BillingAccounts {
  accounts: BillingAccount[];
  self_serve: BillingSelfServe;
}

const KINDS: readonly BillingAccountKind[] = ['personal', 'group', 'firm'];
const RAILS: readonly BillingAccountRail[] = ['card', 'invoice', 'firm_paid', 'comp', 'store', 'none'];
const STATUSES: readonly BillingAccountStatus[] = [
  'active', 'trialing', 'past_due', 'paused', 'cancelling', 'payment_needed', 'lapsed', 'needs_payment_method', 'none',
];
const ATTENTION_TASKS: readonly BillingAttentionTask[] = ['card', 'pay', 'resume'];
const DEEP_LINK_TASKS: readonly BillingDeepLinkTask[] = ['card', 'pay', 'resume', 'cancel', 'keep', 'invoices'];
const ACTIONS: readonly BillingAccountAction[] = ['stripe_portal', 'firm_billing'];

const oneOf = <T extends string>(allowed: readonly T[], value: unknown): T | null =>
  typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : null;

export const isDeepLinkTask = (value: unknown): value is BillingDeepLinkTask => oneOf(DEEP_LINK_TASKS, value) !== null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const text = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value : null);

/** A whole number of zero or more, or null: a count is never a guess. */
const count = (value: unknown): number | null =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;

/** The account key's parts: `personal`, or `group:`/`firm:` and an id. Null for anything else. */
export const parseAccountKey = (key: unknown): { kind: BillingAccountKind; id: string | null } | null => {
  if (key === 'personal') return { kind: 'personal', id: null };
  if (typeof key !== 'string') return null;
  const match = /^(group|firm):([A-Za-z0-9-]+)$/.exec(key);
  return match ? { kind: match[1] as BillingAccountKind, id: match[2] } : null;
};

export const groupAccountKey = (groupId: string): string => `group:${groupId}`;

const toBusiness = (raw: unknown): BillingAccountBusiness | null => {
  if (!isRecord(raw)) return null;
  const id = text(raw.id);
  return id ? { id, name: text(raw.name), owner: raw.owner === true } : null;
};

const toAttention = (raw: unknown): BillingAttention | null => {
  if (!isRecord(raw)) return null;
  const code = text(raw.code);
  return code ? { code, task: oneOf(ATTENTION_TASKS, raw.task) } : null;
};

const toIncludedWith = (raw: unknown): BillingAccount['included_with'] => {
  if (!isRecord(raw)) return null;
  const key = text(raw.key);
  return key ? { key, name: text(raw.name) } : null;
};

/**
 * The group to reinstate. Only ever a group's key: a key of any other kind,
 * or none, is no group to send the person to, and says nothing.
 */
const toReinstateWith = (raw: unknown): BillingReinstateWith | null => {
  if (!isRecord(raw) || parseAccountKey(raw.key)?.kind !== 'group') return null;
  return { key: raw.key as string, name: text(raw.name), task: oneOf(ATTENTION_TASKS, raw.task) };
};

/** A practice's clients. A client with no name is not one the page can show; anything but a list is not said. */
const toFirmBilledClients = (raw: unknown): FirmBilledClient[] | null => {
  if (!Array.isArray(raw)) return null;
  return raw.flatMap(client => {
    if (!isRecord(client)) return [];
    const name = text(client.name);
    return name ? [{ name, rebilled: client.rebilled === true }] : [];
  });
};

/** Who pays, by name; a payer with no name says nothing, and the page says "your accountant's practice". */
const toPaidBy = (raw: unknown): BillingAccount['paid_by'] => {
  const name = isRecord(raw) ? text(raw.name) : null;
  return name ? { name } : null;
};

const toAccount = (raw: unknown): BillingAccount | null => {
  if (!isRecord(raw)) return null;
  const parsed = parseAccountKey(raw.key);
  if (!parsed) return null;
  // The key names the kind; a `kind` that disagrees with it is not trusted.
  const kind = oneOf(KINDS, raw.kind) ?? parsed.kind;
  if (kind !== parsed.kind) return null;

  return {
    key: raw.key as string,
    kind,
    id: parsed.id,
    name: text(raw.name),
    businesses: Array.isArray(raw.businesses)
      ? raw.businesses.map(toBusiness).filter((b): b is BillingAccountBusiness => b !== null)
      : [],
    other_businesses_count: count(raw.other_businesses_count),
    rail: oneOf(RAILS, raw.rail),
    paid_by: toPaidBy(raw.paid_by),
    status: oneOf(STATUSES, raw.status),
    current_period_end: text(raw.current_period_end),
    cancel_at: text(raw.cancel_at),
    included_with: toIncludedWith(raw.included_with),
    // Each read only on the kind of row the api sends it on.
    reinstate_with: kind === 'personal' ? toReinstateWith(raw.reinstate_with) : null,
    free_until: text(raw.free_until),
    seats: count(raw.seats),
    days_remaining: count(raw.days_remaining),
    firm_billed_clients: kind === 'firm' ? toFirmBilledClients(raw.firm_billed_clients) : null,
    // Strict: anything but true is a row the page shows nothing for.
    can_manage: raw.can_manage === true,
    attention: toAttention(raw.attention),
    actions: Array.isArray(raw.actions)
      ? raw.actions.map(action => oneOf(ACTIONS, action)).filter((a): a is BillingAccountAction => a !== null)
      : [],
  };
};

const toSelfServe = (raw: unknown): BillingSelfServe => {
  const flags = isRecord(raw) ? raw : {};
  return { personal: flags.personal === true, group: flags.group === true, firm: flags.firm === true };
};

const readJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const invalid = (status: number) =>
  new SubscriptionApiError(status, INVALID_RESPONSE_CODE, 'Unexpected response from Greenback.');

/**
 * The PRD 00 envelope as the api sends it: `data` holding `accounts` and
 * `self_serve`, with an empty `meta`. Anything else is not a list, and never
 * reads as an empty one.
 */
export const toBillingAccounts = (body: unknown, status = 200): BillingAccounts => {
  const data = isRecord(body) ? body.data : undefined;
  if (!isRecord(data) || !Array.isArray(data.accounts)) throw invalid(status);
  return {
    accounts: data.accounts.map(toAccount).filter((account): account is BillingAccount => account !== null),
    self_serve: toSelfServe(data.self_serve),
  };
};

export const billingAccountsService = {
  /**
   * GET /api/v1/billing/accounts. Database only on the server, so it is cheap
   * enough for the shell to ask on every sign-in. Throws SubscriptionApiError,
   * including a 404 from an api that does not serve it yet.
   */
  async list(): Promise<BillingAccounts> {
    const response = await sendBillingRequest('/api/v1/billing/accounts', { method: 'GET' });
    if (!response.ok) await parseBillingData(response);
    return toBillingAccounts(await readJson(response), response.status);
  },

  /**
   * POST /api/v1/billing/accounts/group/:id/stripe-portal: a Stripe billing
   * portal session for a group the person owns, found by payer, so it works
   * for a signup group with no business yet. Transitional: in-app card, pay
   * and resume replace it (PRD 114 1b, 1c). Refuses 409 `invoice_billed`, 409
   * `firm_billed` for a group its practice pays for with no card of its own,
   * 422 without a Stripe customer, 404 for a group that is not theirs. Throws
   * SubscriptionApiError.
   */
  async openGroupPortal(groupId: string): Promise<{ url: string }> {
    const response = await sendBillingRequest(
      `/api/v1/billing/accounts/group/${encodeURIComponent(groupId)}/stripe-portal`,
      { method: 'POST' }
    );
    const data = await parseBillingData(response);
    return { url: requireWebUrl(data.url, response.status) };
  },
};
